import {AttributesRow} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {useSelector} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {useGetVehicleV2Query} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {useVehicleCatalogue} from '@dms/shared';
import {
  selectBodyStyle,
  selectDrive,
  selectDoorCount,
  selectTransmission,
  selectFuelType,
} from '@dms/teas';

import {Nullish, parseDate} from 'shared/web';

export const useGetVehicleInformation = (
  vehicleId: string | Nullish
): [AttributesRow[], boolean] => {
  const formatDateTime = useDateTimeFormatter();
  const {data: vehicle, isLoading: isLoadingVehicle} = useGetVehicleV2Query(
    {vehicleId: vehicleId ?? ''},
    {skip: isNil(vehicleId)}
  );

  const [vehicleUtils] = useVehicleCatalogue(vehicle?.type);
  const bodyStyle = useSelector(selectBodyStyle(vehicle?.bodyStyle, vehicle?.type));
  const transmission = useSelector(selectTransmission(vehicle?.transmission));
  const driveType = useSelector(selectDrive(vehicle?.drive));
  const doorCountCategory = useSelector(selectDoorCount(vehicle?.doorCountCategory));
  const fuelType = useSelector(selectFuelType(vehicle?.fuelType, vehicle?.type));

  const firstRegistrationOn = isNotNil(vehicle?.firstRegistrationOn)
    ? formatDateTime('dateShort', parseDate(vehicle?.firstRegistrationOn ?? ''))
    : null;

  return [
    [
      {
        label: i18n.t('entity.vehicle.labels.title'),
        value: vehicle?.title,
        testId: testIds.rentalOrder.vehicleAndCustomer('title'),
      },
      {
        label: i18n.t('entity.vehicle.labels.licensePlate'),
        value: vehicle?.registrationPlate,
        testId: testIds.rentalOrder.vehicleAndCustomer('licensePlate'),
      },
      {
        label: i18n.t('entity.vehicle.labels.vin'),
        value: vehicle?.vin,
        testId: testIds.rentalOrder.vehicleAndCustomer('vin'),
      },
      {
        label: i18n.t('entity.vehicle.labels.type'),
        value: vehicleUtils.getVehicleType(vehicle?.type),
        testId: testIds.rentalOrder.vehicleAndCustomer('type'),
      },
      {
        label: i18n.t('entity.vehicle.labels.body'),
        value: bodyStyle?.label,
        testId: testIds.rentalOrder.vehicleAndCustomer('body'),
      },
      {
        label: i18n.t('entity.vehicle.labels.firstRegistration'),
        value: firstRegistrationOn,
        testId: testIds.rentalOrder.vehicleAndCustomer('firstRegistration'),
      },
      {
        label: i18n.t('entity.vehicle.labels.transmission'),
        value: transmission?.label,
        testId: testIds.rentalOrder.vehicleAndCustomer('transmission'),
      },
      {
        label: i18n.t('entity.vehicle.labels.drive'),
        value: driveType?.label,
        testId: testIds.rentalOrder.vehicleAndCustomer('drive'),
      },
      {
        label: i18n.t('entity.vehicle.labels.numberOfDoors'),
        value: doorCountCategory?.label,
        testId: testIds.rentalOrder.vehicleAndCustomer('numberOfDoors'),
      },
      {
        label: i18n.t('entity.vehicle.labels.fuel'),
        value: fuelType?.label,
        testId: testIds.rentalOrder.vehicleAndCustomer('fuel'),
      },
      {
        label: i18n.t('entity.sourcing.vehiclePreview.labels.power'),
        value: isNotNil(vehicle?.power)
          ? `${vehicle?.power.toString()} ${i18n.t('general.metric.kW')}`
          : null,
        testId: testIds.rentalOrder.vehicleAndCustomer('power'),
      },
      {
        label: `${i18n.t('entity.vehicle.labels.engineCapacity')} (${i18n.t('general.metric.ccm')})`,
        value: isNotNil(vehicle?.engineVolume) ? vehicle?.engineVolume.toString() : null,
        testId: testIds.rentalOrder.vehicleAndCustomer('engineCapacity'),
      },
    ],
    isLoadingVehicle,
  ] as const;
};
